import React, { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import PageShell, { Flourish } from '../common/PageShell';
import RichText from '../common/RichText';
import useIsMobile from '../../hooks/useIsMobile';
import caseStudies from '../../data/caseStudies';
import { colors, motion, radius, space, type } from '../../styles/tokens';

const BackLink = () => {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      to="/case-studies"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...type.link,
        display: 'inline-block',
        marginBottom: space[4],
        color: hovered ? colors.inkStrong : colors.sage,
        transition: `color ${motion.fast} ease`
      }}
    >
      <span
        style={{
          display: 'inline-block',
          marginRight: '6px',
          transition: `transform ${motion.fast} ease`,
          transform: hovered ? 'translateX(-3px)' : 'translateX(0)'
        }}
      >
        ←
      </span>
      All case studies
    </Link>
  );
};

// Arrow link that nudges on hover — same behaviour as the card actions.
const ArrowLink = ({ href, children, style }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...type.link,
        color: hovered ? colors.inkStrong : colors.sageDeep,
        transition: `color ${motion.fast} ease`,
        ...style
      }}
    >
      {children}{' '}
      <span
        style={{
          display: 'inline-block',
          transition: `transform ${motion.fast} ease`,
          transform: hovered ? 'translateX(3px)' : 'translateX(0)'
        }}
      >
        →
      </span>
    </a>
  );
};

// Screenshot in a hairline frame. `bleed` pulls it out into the page gutter so
// the hero reads wider than the prose column; desktop only, a phone has no room.
const Figure = ({ image, bleed = false, top = 0, bottom = 0 }) => (
  <figure
    style={{
      marginTop: top,
      marginBottom: bottom,
      marginLeft: bleed ? `-${space[6]}` : 0,
      marginRight: bleed ? `-${space[6]}` : 0
    }}
  >
    <img
      src={image.src}
      alt={image.alt}
      style={{
        display: 'block',
        width: '100%',
        height: 'auto',
        border: `1px solid ${colors.hairline}`,
        borderRadius: radius.md
      }}
    />
    {image.caption && (
      <figcaption
        style={{ ...type.meta, fontStyle: 'italic', lineHeight: 1.5, marginTop: space[1] }}
      >
        {image.caption}
      </figcaption>
    )}
  </figure>
);

// Transparent cut-outs (widgets) side by side on a tinted panel. Each one grows
// in proportion to its aspect ratio, so the row shares a height at every width.
const Gallery = ({ images, isMobile }) => (
  <figure
    style={{
      marginTop: space[5],
      marginBottom: space[5],
      marginLeft: 0,
      marginRight: 0,
      padding: isMobile ? space[3] : space[5],
      background: colors.highlight,
      borderRadius: radius.md,
      display: 'flex',
      justifyContent: 'center'
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? space[2] : space[3],
        width: '100%',
        maxWidth: '560px'
      }}
    >
      {images.map((img, j) => (
        <img
          key={j}
          src={img.src}
          alt={img.alt}
          style={{ flex: `${img.ratio} 1 0px`, minWidth: 0, height: 'auto', display: 'block' }}
        />
      ))}
    </div>
  </figure>
);

const CaseStudyDetail = () => {
  const { id } = useParams();
  const study = caseStudies.find((c) => c.id === id);
  const isMobile = useIsMobile();

  if (!study) {
    return <Navigate to="/case-studies" replace />;
  }

  const styles = {
    h1: {
      ...type.h1(isMobile),
      fontSize: isMobile ? '30px' : '38px',
      marginBottom: space[2]
    },
    h2: {
      ...type.h2,
      margin: `${space[6]} 0 ${space[2]}`
    },
    p: {
      ...type.body,
      marginBottom: space[2]
    },
    metaCard: {
      background: colors.surface,
      border: `1px solid ${colors.hairline}`,
      borderRadius: radius.md,
      padding: isMobile ? space[3] : `${space[3]} ${space[4]}`,
      margin: `${space[4]} 0 ${space[6]}`
    },
    metaRow: {
      display: 'flex',
      gap: space[2],
      alignItems: 'baseline',
      marginBottom: space[1],
      flexWrap: 'wrap',
      fontSize: '15px',
      lineHeight: 1.6
    },
    metaLabel: {
      ...type.label,
      minWidth: '68px',
      flexShrink: 0
    },
    callout: {
      borderLeft: `2px solid ${colors.sageLight}`,
      paddingLeft: space[3],
      margin: `${space[5]} 0`,
      fontSize: '19px',
      lineHeight: 1.65,
      color: colors.inkStrong
    },
    listItem: {
      ...type.body,
      marginBottom: space[1]
    },
    divider: {
      width: '48px',
      height: '1px',
      background: colors.sageLight,
      margin: `${space[6]} 0 ${space[3]}`
    }
  };

  const renderSection = (section, i) => {
    const heading = section.heading ? (
      <h2 style={styles.h2}>
        {section.heading}
        <Flourish tone={colors.sageLight}>✦</Flourish>
      </h2>
    ) : null;

    if (section.type === 'image') {
      return <Figure key={i} image={section} top={space[5]} bottom={space[5]} />;
    }

    if (section.type === 'gallery') {
      return <Gallery key={i} images={section.images} isMobile={isMobile} />;
    }

    if (section.type === 'callout') {
      return (
        <blockquote key={i} style={styles.callout}>
          <RichText>{section.body}</RichText>
        </blockquote>
      );
    }

    if (section.type === 'list') {
      return (
        <div key={i}>
          {heading}
          {section.intro && (
            <p style={styles.p}>
              <RichText>{section.intro}</RichText>
            </p>
          )}
          <ul style={{ paddingLeft: space[2], marginBottom: space[2] }}>
            {section.items.map((item, j) => (
              <li key={j} style={styles.listItem}>
                <RichText>{item}</RichText>
              </li>
            ))}
          </ul>
          {section.outro && (
            <p style={styles.p}>
              <RichText>{section.outro}</RichText>
            </p>
          )}
        </div>
      );
    }

    if (section.type === 'terms') {
      return (
        <div key={i}>
          {heading}
          {section.items.map((item, j) => (
            <p key={j} style={{ ...styles.p, marginBottom: space[2] }}>
              <strong style={{ fontWeight: 600, color: colors.inkStrong }}>{item.label}</strong>{' '}
              <RichText>{item.body}</RichText>
            </p>
          ))}
        </div>
      );
    }

    return (
      <div key={i}>
        {heading}
        {section.body.map((para, j) => (
          <p key={j} style={styles.p}>
            <RichText>{para}</RichText>
          </p>
        ))}
      </div>
    );
  };

  return (
    <PageShell width="prose">
      <BackLink />

      <div style={{ ...type.label, marginBottom: space[2] }}>
        {study.kind} <span style={{ margin: '0 4px' }}>◆</span> {study.year}
      </div>

      <h1 style={styles.h1}>{study.title}</h1>

      <p style={{ ...type.subtitle, fontSize: '15px' }}>
        {study.org} <span style={{ margin: '0 6px' }}>·</span> {study.tech}
      </p>

      {study.hero && (
        <Figure image={study.hero} bleed={!isMobile} top={space[4]} />
      )}

      <div style={styles.metaCard}>
        {study.meta.map((m, i) => (
          <div key={i} style={styles.metaRow}>
            <span style={styles.metaLabel}>{m.label}</span>
            <span style={{ flex: 1, color: colors.ink }}>
              {m.href ? (
                <ArrowLink href={m.href} style={{ fontStyle: 'normal' }}>
                  {m.value}
                </ArrowLink>
              ) : (
                m.value
              )}
            </span>
          </div>
        ))}
      </div>

      {study.sections.map(renderSection)}

      {(study.pdf || study.link) && (
        <>
          <div style={styles.divider}></div>
          <div style={{ display: 'flex', gap: space[3], flexWrap: 'wrap' }}>
            {study.link && (
              <ArrowLink href={study.link}>{study.linkLabel || 'Visit the site'}</ArrowLink>
            )}
          </div>
        </>
      )}
    </PageShell>
  );
};

export default CaseStudyDetail;
